import React, { useState, useEffect, useMemo } from "react";
import { MapInteraction } from "react-map-interaction";
import { feature, merge } from "topojson";
import { line, curveLinearClosed } from "d3-shape";
import sortBy from "lodash.sortby";

import { MapGradients } from "../../Gradients.js";
import { getCountryById } from "../../countryLookup.js";
import {
  scaleProjection,
  fitTransform,
  coordinateToScreen,
  rotationMatrix,
} from "./projectionUtils.js";
import Ripples from "react-ripples";
import countries from "./countries-50m.json";
import globe from "../../images/globe.svg";
import "./styles.scss";

const noop = null;

export const stackLocations = {};
countries.objects.countries.geometries = countries.objects.countries.geometries
  .filter((c) => c.id !== "010")
  .map((c, i) => {
    c.id = c.id || `?-${i}`;
    return c;
  });

/*
Countries missing IDs in countries geometry (c.id === undefined):
  "Somaliland"
  ​"Kosovo"
  ​"N. Cyprus"
  ​"Indian Ocean Ter."
  ​"Ashmore and Cartier Is."
  ​"Siachen Glacier"
*/

const regionCountriesNotInData = {
  Africa: [
    "450",
    "508",
    "516",
    "072",
    "024",
    "434",
    "562",
    "012",
    "430",
    "694",
    "686",
    "478",
    "624",
    "132",
    "480",
    "706",
    "426",
    "748",
    "834",
    "108",
    "646",
    "204",
    "854",
    "140",
    "732",
    "788",
    "232",
    "262",
    "?-58",
    "174",
    "654",
    "690",
    "678",
    "270",
    "260",
    "?-226",
    "334",
    "?-229",
  ],
  "East Asia": ["158", "408"],
  "Europe/Central Asia": [
    "578",
    "208",
    "203",
    "300",
    "348",
    "040",
    "203",
    "703",
    "008",
    "807",
    "191",
    "246",
    "?-130",
    "196",
    "498",
    "705",
    "756",
    "442",
    "440",
    "428",
    "233",
    "248",
    "832",
    "833",
    "020",
    "438",
    "234",
    "?-185",
    "795",
    "031",
    "051",
    "496",
    "336",
    "831",
    "674",
    "492",
  ],
  LAC: [
    "238",
    "858",
    "600",
    "170",
    "862",
    "328",
    "740",
    "591",
    "558",
    "188",
    "340",
    "222",
    "320",
    "084",
    "214",
    "388",
    "044",
    "780",
    "332",
    "308",
    "662",
    "052",
    "659",
    "531",
    "136",
    "796",
    "850",
    "239",
    "660",
    "060",
    "092",
    "500",
    "670",
    "533",
    "663",
    "652",
    "212",
    "028",
    "?-238",
    "534",
  ],
  "Middle East": [
    "887",
    "512",
    "682",
    "368",
    "414",
    "634",
    "275",
    "376",
    "422",
    "760",
    "364",
    "048",
  ],
  "North America": ["630", "666"],
  Oceania: [
    "554",
    "540",
    "548",
    "090",
    "296",
    "776",
    "876",
    "580",
    "882",
    "626",
    "583",
    "584",
    "316",
    "016",
    "612",
    "570",
    "184",
    "520",
    "258",
    "574",
  ],
  "South Asia": ["234", "004", "064", "086", "462"],
  "Southeast Asia": ["585", "096"],
};

const syntheticRegions = [
  {
    key: "Atlantic",
    values: ["304", "352"],
    selectable: false,
  },
  {
    key: "China",
    values: ["156", "446", "344"],
    selectable: false,
  },
];

const settings = {
  scale: {
    min: 1,
    max: 21,
  },
  barSize: 12,
};

function isChina(countryID) {
  return countryID === "156";
}

const isoBar = line()
  .x((d) => d.x)
  .y((d) => d.y)
  .curve(curveLinearClosed);

function generateIsoBar(width, height, yOffset, cap) {
  const pos = {
    x: 0,
    y: -(height + yOffset),
  };
  const iso = {
    x: width * 0.14,
    y: width * 0.44,
  };
  let capOffset = 0;
  if (cap) {
    capOffset = -(iso.y / 2);
  }
  const hexagon = [
    { x: pos.x + -iso.x, y: pos.y + height + iso.y + capOffset }, // bottom point
    { x: pos.x + width / 2, y: pos.y + height + iso.y / 2 + capOffset }, // bottom right
    { x: pos.x + width / 2, y: pos.y }, // top right
    { x: pos.x + iso.x, y: pos.y + -iso.y }, // top point
    { x: pos.x + -width / 2, y: pos.y - iso.y / 2 }, // top left
    { x: pos.x + -width / 2, y: pos.y + height + capOffset }, // bottom left
    { x: pos.x + -iso.x, y: pos.y + height + iso.y + capOffset }, // bottom point
  ];
  return isoBar(hexagon);
}

export default function Map(props) {
  const [transform, setTransform] = useState({
    translation: { x: 0, y: 0 },
    scale: settings.scale.min,
  });
  const [activeGeo, setActiveGeo] = useState(["init"]);

  const {
    transition,
    setTransition,
    yScale,
    width,
    height,
    geoSelection,
    noTransform,
    updateGeoSelection,
    fillSelection,
    stacks,
    geoType,
    updateGeoType,
    setTooltipData,
  } = props;

  const updateTransform = useMemo(
    () =>
      function (transform) {
        const { scale } = transform;
        const stateScale =
          settings.scale.min + (settings.scale.max - settings.scale.min) / 4;
        const selected = geoSelection[0] !== "";
        if (scale > stateScale || selected) {
          if (geoType.geo !== "states") {
            updateGeoType("states");
          }
        } else if (geoType.geo !== geoType.prevGeo) {
          updateGeoType(geoType.prevGeo);
        }
        setTransform(transform);
      },
    [geoType, updateGeoType, geoSelection]
  );

  const map = useMemo(
    () => ({
      countries: feature(countries, countries.objects.countries),
      regions: props.regions.concat(syntheticRegions).map((region) => {
        const countryIDs = region.values.concat(
          regionCountriesNotInData[region.key]
        );
        const feature = merge(
          countries,
          countries.objects.countries.geometries.filter((country) =>
            countryIDs.includes(country.id)
          )
        );
        feature.region = region;
        return feature;
      }),
      outline: merge(countries, countries.objects.countries.geometries),
    }),
    [props.regions]
  );

  const projection = useMemo(
    () => scaleProjection(map.countries, width, height, settings.scale),
    [map.countries, width, height]
  );

  const defaultTransform = useMemo(
    () =>
      fitTransform(
        projection.path,
        map.countries,
        width,
        height,
        settings.scale
      ),
    [projection.path, map.countries, width, height]
  );

  const isDefaultTransform = useMemo(
    () => JSON.stringify(defaultTransform) === JSON.stringify(transform),
    [defaultTransform, transform]
  );

  const runMapTransition = useMemo(
    () =>
      function () {
        setTransition(true);
        const mapTransitionHandle = setTimeout(() => setTransition(false), 700);
        return () => clearTimeout(mapTransitionHandle);
      },
    [setTransition]
  );

  const resetMapTransition = useMemo(
    () =>
      function () {
        const isAll = geoSelection[0] === "";
        runMapTransition();
        if (isAll) {
          updateTransform(defaultTransform);
        } else {
          updateGeoSelection("");
        }
      },
    [
      defaultTransform,
      geoSelection,
      updateTransform,
      updateGeoSelection,
      runMapTransition,
    ]
  );

  useEffect(() => {
    const activeGeoString = activeGeo.join("_");
    const isAll = geoSelection[0] === "";
    if (geoSelection.join("_") !== activeGeoString) {
      if (!noTransform) {
        const transform = isAll
          ? defaultTransform
          : fitTransform(
              projection.path,
              {
                type: "FeatureCollection",
                features: geoSelection.map((g) =>
                  map.countries.features.find((f) => f.id === g)
                ),
                filter: true,
              },
              width,
              height,
              settings.scale
            );
        updateTransform(transform);
        if (activeGeoString !== "init") {
          runMapTransition();
        }
      }
      setActiveGeo(geoSelection);
      setTooltipData(null);
    }
  }, [
    geoSelection,
    noTransform,
    activeGeo,
    map.countries,
    projection.path,
    width,
    height,
    setTooltipData,
    defaultTransform,
    updateTransform,
    runMapTransition,
  ]);

  const paths = useMemo(
    () => ({
      countries: map.countries.features.map((c) => {
        c.country = getCountryById(c.id) || { name: "c-?" };
        c.region = props.regions.find((r) => r.values.includes(c.id)) || {
          key: "r-?",
          values: [],
        };
        c.selectable = !(
          c.country.name.includes("?") || c.region.key.includes("?")
        );
        c.d = projection.path(c);
        return c;
      }),
      regions: map.regions.map((region) => {
        region.d = projection.path(region);
        region.selectable = region.region.hasOwnProperty("selectable")
          ? region.region.selectable
          : true;
        return region;
      }),
      outline: projection.path(map.outline),
    }),
    [map, projection, props.regions]
  );

  paths.countries = useMemo(
    () =>
      paths.countries
        .map((c) => {
          c.stroke = geoSelection.includes(c.id)
            ? "#000000"
            : isChina(c.id)
            ? "#CC0000"
            : "#D9D9D9";
          c.fill = fillSelection[c.country.name] || "#fff";
          return c;
        })
        .sort(
          (a, b) =>
            (geoSelection.includes(a.id) || isChina(a.id) ? 1 : -1) -
            (geoSelection.includes(b.id) || isChina(b.id) ? 1 : -1)
        ),
    [paths.countries, geoSelection, fillSelection]
  );

  paths.regions = useMemo(
    () =>
      paths.regions
        .map((region) => {
          const [countryID] = region.region.values;
          region.stroke = geoSelection.includes(countryID)
            ? "#000000"
            : isChina(countryID)
            ? "#CC0000"
            : "#D9D9D9";
          region.fill = fillSelection[region.region.key] || "#fff";
          return region;
        })
        .sort(
          (a, b) =>
            (geoSelection.includes(a.region.values[0]) ||
            isChina(a.region.values[0])
              ? 1
              : -1) -
            (geoSelection.includes(b.region.values[0]) ||
            isChina(b.region.values[0])
              ? 1
              : -1)
        ),
    [paths.regions, geoSelection, fillSelection]
  );

  const bars = useMemo(
    () =>
      stacks.map((stack) => {
        stack.transform = projection.projection([
          stack.location.longitude,
          stack.location.latitude,
        ]);
        let yOffset = 0;
        stack.bars = stack.groups.map((g) => {
          const nextHeight = yScale(g.count);
          g.path = generateIsoBar(settings.barSize, nextHeight, yOffset, false);
          g.height = nextHeight;
          g.yOffset = yOffset;
          yOffset += nextHeight;
          return g;
        });
        stack.path = stack.total
          ? generateIsoBar(settings.barSize, yOffset, 0, true)
          : null;
        const lastGroup = stack.bars[stack.bars.length - 1];
        if (lastGroup) {
          const { name, color } = lastGroup;
          stack.bars.push({
            name,
            count: 0,
            fill: color,
            key: `${stack.name}-cap`,
            path: generateIsoBar(settings.barSize, 0, yOffset, true),
          });
        }
        return stack;
      }),
    [yScale, stacks, projection]
  );

  function zoom(delta) {
    const scale = Math.max(1, Math.round(transform.scale + delta));
    const { x, y } = transform.translation;

    const scaleOffset0 = {
      x: (width * transform.scale - width) / 2,
      y: (height * transform.scale - height) / 2,
    };

    const translation0 = {
      x: (x + scaleOffset0.x) / transform.scale,
      y: (y + scaleOffset0.y) / transform.scale,
    };

    const scaleOffset = {
      x: (width - width * scale) / 2,
      y: (height - height * scale) / 2,
    };

    const translation = {
      x: translation0.x * scale + scaleOffset.x,
      y: translation0.y * scale + scaleOffset.y,
    };

    updateTransform({ scale, translation });
  }

  let colorScale = null;
  if (props.colorScale) {
    let colorScaleTicks = props.colorScale.ticks();
    if (colorScaleTicks.length) {
      colorScaleTicks = [0].concat(colorScaleTicks);
    }
    colorScale = (
      <div className="colorScale">
        {colorScaleTicks.map((tick, tickIndex) => {
          if (
            tickIndex !== 0 &&
            tickIndex !== colorScaleTicks.length - 1 &&
            tickIndex % 2 === 0
          ) {
            return null;
          }
          return (
            <div className="tick" key={tick}>
              <div
                className="tickFill"
                style={{
                  backgroundColor: tick ? props.colorScale(tick) : "#fff",
                }}
              />
              <div className="tickLabel">{Math.round(tick / 1000)}k MW</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className="sizeContainer"
      style={{ width, height, pointerEvents: transition ? "none" : "auto" }}
    >
      <div className="controls">
        <div className="rippleButton">
          <Ripples>
            <button
              type="button"
              disabled={isDefaultTransform}
              className={`${isDefaultTransform ? "inactive" : ""}`}
              style={{ width: "30px", paddingTop: "5px" }}
              onClick={resetMapTransition}
            >
              <img src={globe} alt="Reset" />
            </button>
          </Ripples>
        </div>
        <div className="rippleButton">
          <Ripples>
            <button
              type="button"
              disabled={transform.scale >= settings.scale.max}
              className={`${
                transform.scale >= settings.scale.max ? "inactive" : ""
              }`}
              style={{ width: "30px", paddingTop: "5px" }}
              onClick={() => zoom(1)}
            >
              +
            </button>
          </Ripples>
        </div>
        <div className="rippleButton">
          <Ripples>
            <button
              type="button"
              disabled={transform.scale <= settings.scale.min}
              className={`${
                transform.scale <= settings.scale.min ? "inactive" : ""
              }`}
              onClick={() => zoom(-1)}
            >
              -
            </button>
          </Ripples>
        </div>
      </div>
      {colorScale}
      <MapInteraction
        showControls={false}
        disableZoom={true}
        controlsClass="controls"
        minScale={settings.scale.min}
        maxScale={settings.scale.max}
        scale={transform.scale}
        translation={transform.translation}
        onChange={updateTransform}
      >
        {({ translation, scale }) => {
          const { x, y } = translation;
          stackLocations.transform = {
            translate: translation,
            scale,
          };
          return (
            <div
              className={`mapContainer ${transition ? "transition" : ""}`}
              onDoubleClick={(e) => {
                const { target } = e;
                const { id } = target;
                if (id === "Map") {
                  resetMapTransition();
                }
              }}
            >
              <svg
                key="Map"
                id="Map"
                className="Map"
                width={props.width}
                height={props.height}
              >
                <g
                  key="animated"
                  className="transformContainer"
                  style={{
                    transform: `translate(${x}px, ${y}px) scale(${scale})`,
                  }}
                >
                  <MapRenderer
                    width={width}
                    height={height}
                    scale={scale}
                    paths={paths}
                    valueKey={props.valueKey}
                    geoSelection={geoSelection}
                    bars={bars}
                    years={props.years}
                    geoType={props.geoType}
                    updateGeoSelection={updateGeoSelection}
                    tooltipData={props.tooltipData}
                    setTooltipData={props.setTooltipData}
                    extraGradients={props.extraGradients}
                    lockedProject={props.lockedProject}
                    updateLockedProject={props.updateLockedProject}
                  />
                </g>
              </svg>
            </div>
          );
        }}
      </MapInteraction>
    </div>
  );
}

function MapRenderer(props) {
  const {
    paths,
    years,
    valueKey,
    setTooltipData,
    geoSelection,
    updateGeoSelection,
    lockedProject,
    updateLockedProject,
  } = props;

  const stacks = useMemo(
    () =>
      sortBy(
        props.bars.map((s) => {
          s.screen = coordinateToScreen(s.transform, props.width, props.height);
          return s;
        }),
        (s) => s.screen[1]
      ),
    [props.bars, props.width, props.height]
  );

  const inStateView = props.geoType.geo === "states";

  const hoverItem = useMemo(
    () =>
      function (type) {
        return (selection) => {
          return (event) => {
            if (!selection) {
              setTooltipData(null);
              return;
            }
            let x = 0;
            let y = 0;
            if ("pageX" in event) {
              x = event.pageX;
              y = event.pageY;
            }
            const lockable = type === "project";
            setTooltipData({
              data: selection,
              type: type,
              lockable,
              valueKey,
              x,
              y,
              hoveredFromMap: true,
            });
          };
        };
      },
    [setTooltipData, valueKey]
  );

  const hoverCountry = useMemo(
    () =>
      function (event, country) {
        let selection = null;
        if (country && geoSelection[0] === "") {
          selection = {
            name: country.country.name,
            id: country.id,
            color: country.fill,
            years: [],
          };
          years.forEach((y) => {
            const groups = y.groups
              .map((g) => g.projects)
              .reduce((a, v) => a.concat(v), [])
              .filter((g) => g.countryID === country.id);
            const total = groups.map((g) => g.count).reduce((a, v) => a + v, 0);
            selection.years.push({
              year: y.name,
              groups,
              total,
            });
          });
          selection.years = selection.years.filter((y) => y.total > 0);
          if (!selection.years.length) {
            selection = null;
          }
        }
        hoverItem("capacity")(selection)(event);
      },
    [geoSelection, years, hoverItem]
  );

  const hoverRegion = useMemo(
    () =>
      function (event, region) {
        let selection = null;
        if (region && geoSelection[0] === "") {
          selection = {
            name: region.region.key,
            id: region.region.key,
            color: region.fill,
            years: [],
          };
          years.forEach((y) => {
            const groups = y.groups
              .map((g) => g.projects)
              .reduce((a, v) => a.concat(v), [])
              .filter((g) => region.region.values.includes(g.countryID));
            const total = groups.map((g) => g.count).reduce((a, v) => a + v, 0);
            selection.years.push({
              year: y.name,
              groups,
              total,
            });
          });
          selection.years = selection.years.filter((y) => y.total > 0);
          if (!selection.years.length) {
            selection = null;
          }
        }
        hoverItem("capacity")(selection)(event);
      },
    [geoSelection, years, hoverItem]
  );

  const selectCountry = useMemo(
    () =>
      function (name) {
        updateGeoSelection(name);
        hoverItem("country")(null)();
      },
    [hoverItem, updateGeoSelection]
  );

  const clickCountry = useMemo(
    () =>
      function (country) {
        if (country.selectable && !geoSelection.includes(country.id)) {
          hoverCountry(null, null);
          selectCountry(country.country.name);
        }
      },
    [hoverCountry, geoSelection, selectCountry]
  );

  const clickRegion = useMemo(
    () =>
      function (region) {
        if (region.selectable) {
          hoverRegion(null, null);
          selectCountry(region.region.key);
        }
      },
    [hoverRegion, selectCountry]
  );

  const countries = useMemo(
    () =>
      paths.countries.map((c) => (
        <path
          className="country"
          key={c.id}
          d={c.d}
          stroke={c.stroke}
          fill={c.fill}
          style={{ cursor: c.selectable ? "pointer" : "default" }}
          onMouseMove={stacks.length ? noop : (e) => hoverCountry(e, c)}
          onMouseOut={stacks.length ? noop : (e) => hoverCountry(e, null)}
          onClick={() => clickCountry(c)}
          onTouchStart={() => clickCountry(c)}
        />
      )),
    [paths.countries, hoverCountry, clickCountry, stacks.length]
  );

  const regions = useMemo(
    () =>
      paths.regions.map((region) => (
        <path
          className="region"
          key={region.region.key}
          d={region.d}
          stroke={region.stroke}
          fill={region.fill}
          style={{
            cursor:
              region.selectable && region.stroke !== "#000000"
                ? "pointer"
                : "default",
          }}
          onMouseMove={stacks.length ? noop : (e) => hoverRegion(e, region)}
          onMouseOut={stacks.length ? noop : (e) => hoverRegion(e, null)}
          onClick={() => clickRegion(region)}
          onTouchStart={() => clickRegion(region)}
        />
      )),
    [paths.regions, stacks.length, hoverRegion, clickRegion]
  );

  const shadows = useMemo(
    () =>
      stacks.map((s) => (
        <path
          key={`${s.name}-shadow`}
          className="shadow"
          transform={`translate(${s.transform}) scale(${1 / props.scale})`}
          d={s.path}
          fill="url(#shadow)"
        />
      )),
    [stacks, props.scale]
  );

  const stacksX = (props.scale / settings.scale.max) * 0.095;
  const stacksY = (props.scale / settings.scale.max) * -0.68;
  const stackedBars = useMemo(
    () =>
      stacks.map((s) => (
        <g
          key={s.name}
          className="stack"
          transform={`translate(${s.screen}) scale(${1 / props.scale})`}
          onMouseMove={inStateView ? noop : hoverItem("country")(s)}
          onMouseOut={inStateView ? noop : hoverItem("country")(null)}
          onClick={inStateView ? noop : () => selectCountry(s.name)}
          onTouchStart={inStateView ? noop : () => selectCountry(s.name)}
        >
          {s.bars.map((g) => {
            const locked =
              lockedProject && g.projects
                ? lockedProject.plantID === g.projects[0].plantID
                : false;
            const x = stacksX;
            const y = stacksY + (-(g.yOffset || 0) + 50) / props.scale;
            stackLocations[g.key] = {
              translate: {
                x,
                y,
              },
              screen: s.screen,
            };

            return (
              <path
                data-stack={g.key}
                key={g.key}
                d={g.path}
                stroke="#000"
                fill={g.fill}
                opacity={locked || !lockedProject ? 1 : 0.3}
                filter={locked ? "url(#black-glow)" : ""}
                onMouseMove={inStateView ? hoverItem("project")(g) : noop}
                onMouseOut={inStateView ? hoverItem("project")(null) : noop}
                onClick={
                  inStateView
                    ? () => updateLockedProject(g.projects[0], false, true)
                    : noop
                }
                onTouchStart={
                  inStateView
                    ? () => updateLockedProject(g.projects[0], false, true)
                    : noop
                }
              />
            );
          })}
        </g>
      )),
    [
      hoverItem,
      inStateView,
      lockedProject,
      props.scale,
      selectCountry,
      stacks,
      updateLockedProject,
      stacksX,
      stacksY,
    ]
  );

  return (
    <g id="MapRenderer" key="MapRenderer" className="MapRenderer">
      <MapGradients>{props.extraGradients}</MapGradients>
      <g
        key="countries"
        className="countries"
        transform={`
          translate(${props.width / 2}, ${props.height / 2})
          matrix(${rotationMatrix})
          translate(${-props.width / 2}, ${-props.height / 2})
        `}
        style={{ strokeWidth: 1 / props.scale }}
      >
        <path
          key="background"
          className="background"
          transform={`translate(${-2 / props.scale}, ${2 / props.scale})`}
          d={paths.outline}
        />
        {props.geoType.geo === "regions" ||
        (inStateView && props.geoType.prevGeo === "regions")
          ? regions
          : countries}
        {shadows}
      </g>
      <g
        key="stacks"
        className="stacks"
        transform={`translate(${stacksX}, ${stacksY})`}
      >
        {stackedBars}
      </g>
    </g>
  );
}
